import { QuoteDocumentConfigPositionEnum } from '@monetize/types/app';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import type { SetStateAction } from 'react';
import { resolveSetStateAction } from './setStateAction';

export type QuoteDocumentUploadStatus =
  | 'selecting-type'
  | 'uploading'
  | 'error';

export interface QuoteDocumentUpload {
  localId: string;
  /** Id of the quote or legal entity this pending upload belongs to. */
  ownerId: string;
  file: File;
  position: QuoteDocumentConfigPositionEnum;
  status: QuoteDocumentUploadStatus;
  documentTypeId: string | null;
  errorMessage: string | null;
}

/**
 * Flat, app-wide list of in-progress quote document uploads (from both the
 * Quote page and the Settings > Legal Entity document library, since they
 * share QuoteDocumentsList). The Jotai Provider is mounted above the router (App.tsx),
 * so this survives the Internal/External view toggle (which fully
 * unmounts/remounts its subtree) and leaving the page entirely and coming
 * back — plain component state cannot.
 */
export const quoteDocumentUploadsState = atom<QuoteDocumentUpload[]>([]);

/**
 * Read/write slice of quoteDocumentUploadsState scoped to a single ownerId.
 * Mirrors the tableFilterSelector pattern in global.store.ts.
 */
export const quoteDocumentUploadsForOwnerSelector = atomFamily(
  (ownerId: string) =>
    atom(
      (get) =>
        get(quoteDocumentUploadsState).filter(
          (upload) => upload.ownerId === ownerId,
        ),
      (get, set, update: SetStateAction<QuoteDocumentUpload[]>) => {
        const uploads = get(quoteDocumentUploadsState);
        const others = uploads.filter((upload) => upload.ownerId !== ownerId);
        const owned = uploads.filter((upload) => upload.ownerId === ownerId);
        set(quoteDocumentUploadsState, [
          ...others,
          ...resolveSetStateAction(update, owned),
        ]);
      },
    ),
);
